import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaImage, FaPalette, FaMoon, FaSun, FaSave } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useThemeConfig } from '../context/ThemeConfigContext';
import './Settings.css';

const SWATCHES = ['#1a5c5a', '#154a48', '#0b3d91', '#5b2c6f', '#7d3c02', '#1a1a1a', '#8e0e2c'];

export default function Settings() {
  const navigate = useNavigate();
  const { config, loading, saveConfig, agentCode } = useThemeConfig();

  const [sidebarColor, setSidebarColor] = useState(config.sidebarColor);
  const [theme, setTheme] = useState(config.theme);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sync local form state once the agent's saved config has loaded (or changes elsewhere).
  useEffect(() => {
    setSidebarColor(config.sidebarColor);
    setTheme(config.theme);
  }, [config]);

  useEffect(() => {
    if (!loading && !agentCode) {
      navigate('/login');
    }
  }, [loading, agentCode, navigate]);

  const handleLogoChange = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }, []);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await saveConfig({ sidebarColor, theme, logoFile });
      if (result.success) {
        toast.success('Settings saved');
        setLogoFile(null);
        setLogoPreview(null);
      } else {
        toast.error(result.message || 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  }, [saveConfig, sidebarColor, theme, logoFile]);

  if (loading) return null;

  return (
    <div className="settings-page">
      <Header userName="Settings" />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="settings-main">
        <div className="settings-top-row">
          <span className="settings-company">DEFINITE ASSURANCE</span>
          <span className="settings-breadcrumb">Dashboard / Settings</span>
        </div>

        <form className="settings-card" onSubmit={handleSave}>
          <h3><FaCog style={{ marginRight: 8 }} />Appearance Settings</h3>
          <p className="settings-subtext">
            These preferences are saved to your account and applied every time you sign in.
          </p>

          {/* ── Logo ── */}
          <div className="settings-section">
            <h4><FaImage style={{ marginRight: 6 }} />Logo</h4>
            <div className="settings-logo-row">
              <img
                src={logoPreview || config.logo}
                alt="Current logo"
                className="settings-logo-preview"
              />
              <div>
                <input type="file" accept="image/*" onChange={handleLogoChange} />
                <p className="settings-hint">Shown in the header in place of the default Definite Assurance logo.</p>
              </div>
            </div>
          </div>

          {/* ── Sidebar color ── */}
          <div className="settings-section">
            <h4><FaPalette style={{ marginRight: 6 }} />Sidebar Color</h4>
            <div className="settings-color-row">
              <input
                type="color"
                value={sidebarColor}
                onChange={(e) => setSidebarColor(e.target.value)}
                className="settings-color-input"
              />
              <div className="settings-swatches">
                {SWATCHES.map((swatch) => (
                  <button
                    type="button"
                    key={swatch}
                    className={`settings-swatch ${sidebarColor === swatch ? 'active' : ''}`}
                    style={{ backgroundColor: swatch }}
                    onClick={() => setSidebarColor(swatch)}
                    aria-label={swatch}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ── Theme ── */}
          <div className="settings-section">
            <h4>Theme</h4>
            <div className="settings-theme-row">
              <button
                type="button"
                className={`settings-theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <FaSun /> Light
              </button>
              <button
                type="button"
                className={`settings-theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <FaMoon /> Dark
              </button>
            </div>
          </div>

          <button type="submit" className="settings-save-btn" disabled={saving}>
            <FaSave style={{ marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </main>
    </div>
  );
}
